import React, { useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as CopyIcon } from "assets/icons/copy.svg";
import { BusinessContentItem } from "features/business-compliance/selected-business/BusinessContentItem";
import { IBusinessContentItem } from "features/business-compliance/selected-business/types";
import { handleCopyToClipboard } from "utils";
import { START_INDEX } from "utils/constants";
import { maskRandomDigits } from "utils/general/mask-data";

import { FALL_BACK_IMAGE_URL, ImageViewer } from "ui/image-viewer";
import { PdfViewer } from "ui/pdf-viewer";
import { Heading4 } from "ui/typography";

import { IBusinessDetailsDirectorInformationProps } from "./types";

export const identityDocumentLabel: Record<string, string> = {
  "driver's-license": "Driver's License",
  "international-passport": "International Passport",
  "national-id": "National ID Card or NIN Slip",
  "voter's-card": "Voter's Card",
};

export const BusinessDetailsDirectorInformation = (
  props: IBusinessDetailsDirectorInformationProps
) => {
  const {
    businessDirector,
    isContentWrapperBelow600,
    isContentWrapperBelow745,
    position,
    business,
  } = props;
  const [isError, setIsError] = useState(false);
  const [showIdDocument, setShowIdDocument] = useState(false);

  const handleOpenIdDocument = () => {
    setShowIdDocument(true);
  };

  const handleCloseIdDocument = () => {
    setShowIdDocument(false);
  };

  const handleCopyBvn = async () => {
    await handleCopyToClipboard(
      businessDirector.bvn ?? "",
      "BVN Copied Successfully!"
    );
  };

  const businessDirectorList: IBusinessContentItem[] = useMemo(
    () => [
      {
        title: "First Name",
        content: businessDirector.directorFirstName,
      },
      {
        title: "Last Name",
        content: businessDirector.directorLastName,
      },
      {
        title: "Bank Verification Number",
        content: (
          <Box className="flex items-center gap-2">
            <p>{maskRandomDigits(businessDirector?.bvn) || "N/A"}</p>
            <CopyIcon
              cursor="pointer"
              onClick={handleCopyBvn}
            />
          </Box>
        ),
      },
      {
        title: "Date of Birth",
        content: businessDirector.directorDob,
      },
      /* {
        title: "Nationality",
        content: businessDirector.nationality || "-",
      }, */
      {
        title: "ID Type",
        content: identityDocumentLabel?.[businessDirector.documentType] || "-",
      },
      {
        title: "ID Document",
        content: (
          <Box className="mt-2">
            {businessDirector.documentFile.endsWith(".pdf") ? (
              <PdfViewer
                pdfUrl={businessDirector.documentFile}
                isContentWrapperBelow745={isContentWrapperBelow745}
                isContentWrapperBelow600={isContentWrapperBelow600}
              />
            ) : (
              <Box
                className={`border-[#E4E4E4] border-[1px] rounded-[7.43px] ${isContentWrapperBelow600 ? "w-fit h-[235px]" : "h-[203.36px] w-[230px]"}`}
              >
                <Box
                  css={{
                    position: "relative",
                    width: "100%",

                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "0",
                      left: "0",
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#00000045",
                      zIndex: 1,
                      borderTopLeftRadius: "7.43px",
                      borderTopRightRadius: "7.43px",
                    },
                  }}
                >
                  <img
                    src={
                      isError
                        ? FALL_BACK_IMAGE_URL
                        : businessDirector.documentFile
                    }
                    alt={`${businessDirector.documentType}'s document image`}
                    className="object-cover h-[132.79px] w-full rounded-t-[7.43px]"
                    onError={() => setIsError(true)}
                  />
                </Box>
                <Box
                  className={`flex gap-[11.14px] items-center px-[15px] h-[calc(100%-132.79px)] ${isContentWrapperBelow600 || isContentWrapperBelow745 ? "justify-center" : ""}`}
                >
                  <span
                    onClick={handleOpenIdDocument}
                    className="text-[#00535C] text-sm leading-[22.29px] font-medium h-[41.57px] w-[109.57px] rounded-[7.43px] py-[9.29px] px-[16.71px] border-[1px] border-[#D0D5DD] text-center cursor-pointer"
                  >
                    View
                  </span>
                  <a
                    href={businessDirector.documentFile}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00535C] text-sm leading-[22.29px] font-medium h-[41.57px] w-[109.57px] rounded-[7.43px] py-[9.29px] px-[16.71px] border-[1px] border-[#D0D5DD] text-center"
                  >
                    Download
                  </a>
                </Box>
              </Box>
            )}
          </Box>
        ),
      },
    ],
    [isContentWrapperBelow745, isContentWrapperBelow600, isError]
  );

  return (
    <Box>
      <Heading4
        fontSize="12px"
        lineHeight="24px"
        fontWeight="700"
        textColor="#374151"
        className="mb-4"
      >
        DIRECTOR &nbsp; {position}
      </Heading4>
      <Box
        css={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "48px",
          rowGap: "16px",
        }}
      >
        {businessDirectorList.map(({ title, content }, index) => (
          <BusinessContentItem
            key={index}
            title={title}
            content={content}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            businessContentItemClassName="basis-[29.2%]"
          />
        ))}
      </Box>
      <ImageViewer
        imageDataList={[
          {
            title: "ID Document",
            imageUrl: businessDirector.documentFile,
          },
        ]}
        isVisible={showIdDocument}
        imageIndex={START_INDEX}
        handleCloseImageViewer={handleCloseIdDocument}
        uploaderName={business?.business?.email || "N/A"}
      />
    </Box>
  );
};
